/**
 * ProviderPortalAccessDialog - Liberar, reenviar ou bloquear o acesso do prestador ao portal.
 *
 * O prestador ativa o acesso em /prestador/:slug/ativar informando o e-mail convidado e o CPF/CNPJ
 * do cadastro. Nenhuma senha ou link de acesso é gerado aqui (anti-sequestro de conta).
 *
 * RELEASE: 2026-10-12 v1.0.0 - Versão inicial (Fase 1 do Portal do Prestador).
 */
import { useEffect, useState } from 'react';
import { Copy, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FieldLabel } from '@/components/common/FormSection';
import { InlineNotice } from '@/components/ui/inline-notice';
import { providerPortalUrl, useProviderPortalAccess, useProviderPortalSettings } from '@/hooks/useProviderPortalAdmin';

const STATUS_LABEL = { pending: 'Aguardando ativação', active: 'Ativo', blocked: 'Bloqueado' } as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: { id: string; name: string; email: string | null } | null;
}

export function ProviderPortalAccessDialog({ open, onOpenChange, person }: Props) {
  const { settings } = useProviderPortalSettings();
  const { access, isLoading, invite, setBlocked } = useProviderPortalAccess(open ? person?.id ?? null : null);
  const [email, setEmail] = useState('');

  useEffect(() => { if (open) setEmail(access?.email ?? person?.email ?? ''); }, [open, access?.email, person?.email]);

  const link = settings?.slug ? `${providerPortalUrl(settings.slug)}/ativar` : '';
  const copy = () => { navigator.clipboard.writeText(link); toast.success('Link copiado'); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        <PremiumDialogHeader icon={KeyRound} title="Acesso ao Portal do Prestador" />
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm font-medium">{person?.name}</p>
          {!settings?.enabled && (
            <InlineNotice variant="warning">Habilite o Portal do Prestador em Configurações › Ordem de Serviço.</InlineNotice>
          )}
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : access && (
            <div className="flex items-center gap-2 text-sm">
              Situação: <Badge variant="secondary">{STATUS_LABEL[access.status]}</Badge>
            </div>
          )}
          {access?.status !== 'active' && access?.status !== 'blocked' && (
            <div className="space-y-1.5">
              <FieldLabel required>E-mail de acesso</FieldLabel>
              <Input type="email" value={email} maxLength={255} onChange={(e) => setEmail(e.target.value.trim())} />
            </div>
          )}
          {access?.status === 'pending' && link && (
            <div className="space-y-1.5">
              <FieldLabel>Envie este link ao prestador</FieldLabel>
              <div className="flex gap-2">
                <Input readOnly value={link} className="text-xs" />
                <Button type="button" variant="outline" size="icon" onClick={copy} aria-label="Copiar link"><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Ele cria a senha informando o e-mail acima e o CPF/CNPJ do cadastro.</p>
            </div>
          )}
        </div>
        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          {access && access.status !== 'pending' && (
            <Button variant="outline" disabled={setBlocked.isPending} onClick={() => setBlocked.mutate(access.status !== 'blocked')}>
              {access.status === 'blocked' ? 'Desbloquear' : 'Bloquear acesso'}
            </Button>
          )}
          {access?.status === 'pending' && (
            <Button variant="outline" disabled={setBlocked.isPending} onClick={() => setBlocked.mutate(true)}>Bloquear</Button>
          )}
          {(!access || access.status === 'pending') && (
            <Button disabled={!settings?.enabled || !email || invite.isPending} onClick={() => invite.mutate(email)}>
              {invite.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              {access ? 'Atualizar convite' : 'Liberar acesso'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
